import React from 'react';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { Badge, Box, SimpleGrid, Tooltip } from '@chakra-ui/react';
import moment from 'moment/moment';
import 'moment/locale/is';
import { STATUS_MAPPER } from 'utils/statusUtils';
import { ActionsCell } from './ActionsCell';
import { TravelRequestResult } from 'api/types/types';

export const columns = (
  role: string,
  adminPath: string,
  hideHeaders?: boolean
): ColumnDef<TravelRequestResult, any>[] => {
  return [
    {
      accessorKey: 'id',
      enableSorting: true,
      header: () => (hideHeaders ? null : <span>Beiðni</span>),
      accessorFn: (row) => row,
      cell: ({ getValue }): JSX.Element => {
        const {
          id,
          erpId,
          country,
          city,
          departureDate,
          arrivalDate,
          departureTime,
          arrivalTime,
          status,
          createdByName,
          employeeName,
          lastChanged,
        } = getValue() as TravelRequestResult;
        const { shortName } = status;
        return (
          <SimpleGrid columns={{ base: 1, md: 2 }}>
            {lastChanged && (
              <div
                style={{
                  position: 'absolute',
                  width: '3px',
                  background: 'orange',
                  top: '0',
                  left: '-3px',
                  bottom: '0',
                }}
              ></div>
            )}
            <div style={{ display: 'flex' }}>
              <div>
                {lastChanged && (
                  <Tooltip
                    label={
                      <span>
                        <strong>Síðasta breyting:</strong>{' '}
                        {moment(lastChanged).format('DD. MMMM')}
                      </span>
                    }
                  >
                    <span
                      style={{
                        color: 'gray',
                        fontSize: '90%',
                      }}
                    >
                      {id}
                    </span>
                  </Tooltip>
                )}
                {!lastChanged && (
                  <span
                    style={{
                      color: 'gray',
                      fontSize: '90%',
                    }}
                  >
                    {id}
                  </span>
                )}
                <span
                  style={{ color: 'gray', fontSize: '90%', margin: '0 10px' }}
                >
                  |
                </span>
              </div>
              <div style={{ marginRight: '10px' }}>
                {role && (
                  <Tooltip label={<span>Beiðni gerð af: {createdByName}</span>}>
                    <strong>{employeeName}</strong>
                  </Tooltip>
                )}
                {!role && (
                  <Tooltip
                    label={
                      <span>
                        <strong>Brottför:</strong>{' '}
                        {moment(departureDate).format('DD. MMMM')}
                        {', '}
                        {departureTime.toLowerCase()} <br />
                        <strong>Koma:</strong>{' '}
                        {moment(arrivalDate).format('DD. MMMM')}
                        {', '}
                        {arrivalTime.toLowerCase()}
                        {lastChanged && (
                          <>
                            <br />
                            <strong>Síðasta breyting:</strong>{' '}
                            {moment(lastChanged).format('DD. MMMM')}
                          </>
                        )}
                      </span>
                    }
                  >
                    <strong>
                      {shortName !== 'DRAFT'
                        ? `${country}, ${city}`
                        : 'Drög að ferðabeiðni'}
                    </strong>
                  </Tooltip>
                )}
                {erpId && (
                  <Tooltip label="Kostnaðarskýrsla ferðabeiðnar">
                    <span style={{ color: 'gray', fontSize: '90%' }}>
                      {' '}
                      - {erpId}
                    </span>
                  </Tooltip>
                )}
                {!role && (
                  <div>
                    <Tooltip
                      label={`Ferð hefst þann ${moment(departureDate).format(
                        'DD. MMMM YYYY'
                      )}`}
                    >
                      <span style={{ color: 'gray', fontSize: '90%' }}>
                        {moment(departureDate).format('DD. MMMM')}{' '}
                        {departureTime.toLowerCase()} -{' '}
                        {moment(arrivalDate).format('DD. MMM')}{' '}
                        {arrivalTime.toLowerCase()}
                      </span>
                    </Tooltip>
                  </div>
                )}
                <Box mt={1}>
                  {!role && (
                    <Tooltip
                      label={<span>Beiðni gerð af: {createdByName}</span>}
                    >
                      <span style={{ color: 'gray', fontSize: '90%' }}>
                        <strong>Ferðalangur:</strong> {employeeName}
                      </span>
                    </Tooltip>
                  )}
                  {role && (
                    <Tooltip
                      label={
                        <span>
                          <strong>Brottför:</strong>{' '}
                          {moment(departureDate).format('DD. MMMM')}
                          {', '}
                          {departureTime.toLowerCase()} <br />
                          <strong>Koma:</strong>{' '}
                          {moment(arrivalDate).format('DD. MMMM')}
                          {', '}
                          {arrivalTime.toLowerCase()}
                        </span>
                      }
                    >
                      <span style={{ color: 'gray', fontSize: '90%' }}>
                        <strong>
                          {shortName !== 'DRAFT'
                            ? `${country}, ${city}`
                            : 'Drög að ferðabeiðni'}
                        </strong>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </div>
            </div>
          </SimpleGrid>
        );
      },
    },
    {
      accessorKey: 'departureDate',
      enableSorting: true,
      sortDescFirst: true,
      header: () => (hideHeaders ? null : <span>Brottför</span>),
      cell: (props: CellContext<TravelRequestResult, any>): string => {
        const departureDate = props.getValue();
        return moment(departureDate).format('DD. MMM. YYYY');
      },
    },
    {
      accessorKey: 'arrivalDate',
      enableSorting: true,
      header: () => (hideHeaders ? null : <span>Koma</span>),
      cell: (props: CellContext<TravelRequestResult, any>): string => {
        const arrivalDate = props.getValue();
        return moment(arrivalDate).format('DD. MMM. YYYY');
      },
    },
    {
      accessorKey: 'status',
      enableSorting: true,
      header: () => (hideHeaders ? null : <span>Staða</span>),
      cell: (props: CellContext<TravelRequestResult, any>): JSX.Element => {
        const { description, shortName } = props.getValue();
        return (
          <Tooltip label={description}>
            <Badge
              colorScheme={STATUS_MAPPER[shortName]?.color}
              cursor="pointer"
            >
              {STATUS_MAPPER[shortName]?.label}
            </Badge>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'actions',
      header: () =>
        hideHeaders ? null : (
          <Box display="flex" marginLeft="auto">
            Aðgerðir
          </Box>
        ),
      accessorFn: (row) => row,
      cell: ({ getValue }): JSX.Element | undefined => {
        const { id, status } = getValue() as TravelRequestResult;
        const { shortName } = status;
        return (
          <Box float="right">
            <ActionsCell
              id={id}
              role={role}
              adminPath={adminPath}
              statusName={shortName}
            />
          </Box>
        );
      },
    },
  ];
};
